import Helper from '@ember/component/helper';
import { task, type Task } from 'ember-concurrency';

type TaskType = 'drop' | 'enqueue' | 'keepLatest' | 'restartable';
type HandlerFn = (...args: unknown[]) => unknown;

export interface EcTaskSignature {
  Args: {
    Positional: [HandlerFn];
    Named: {
      type?: TaskType;
    };
  };
  Return: Task<unknown, [...unknown[]]>;
}

export default class EcTask extends Helper<EcTaskSignature> {
  /**
   * This helper wraps a function in an ember-concurrency task
   * Useful when you are passing a fn to a component that expects an ember-concurrency task
   *
   * @example
   * ```<ChildComponent @handleClick={{ecTask this.myFunction type="drop"}} />```
   *
   * @param {Function} fn   Required. The function to wrap
   * @param {String} type   Optional. The type of task. Defaults to `task`
   *
   * @returns {Task}        Returns an ember concurrency task
   */
  handler!: HandlerFn;

  task = task(async (...args: unknown[]) => {
    return await this.handler(...args);
  });

  dropTask = task({ drop: true }, async (...args: unknown[]) => {
    return await this.handler(...args);
  });

  restartableTask = task({ restartable: true }, async (...args: unknown[]) => {
    return await this.handler(...args);
  });

  enqueueTask = task({ enqueue: true }, async (...args: unknown[]) => {
    return await this.handler(...args);
  });

  keepLatestTask = task({ keepLatest: true }, async (...args: unknown[]) => {
    return await this.handler(...args);
  });

  compute(
    [handler]: EcTaskSignature['Args']['Positional'],
    { type }: EcTaskSignature['Args']['Named'],
  ): Task<unknown, [...unknown[]]> {
    this.handler = handler;

    switch (type) {
      case 'drop':
        return this.dropTask;
      case 'enqueue':
        return this.enqueueTask;
      case 'keepLatest':
        return this.keepLatestTask;
      case 'restartable':
        return this.restartableTask;
      default:
        return this.task;
    }
  }
}
